import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  Box,
  Display,
  FontFamily,
  FontWeight,
  Heading2,
  JustifyContent,
  Space,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'
import { CWC_HOW_IT_WORKS_SECTION } from './config'
import { CWCHowItWorksCarousel } from './components/CWCHowItWorksCarousel'

// Section heading — Axiforma ExtraBold 36/40, sentence case, centred, colour #1A2026 (Mirage/ink)
// per Figma 371:12578. The mobile frame wraps it to two lines naturally.
// Size comes from the Citrus `size` prop (13 = 36/40) on the element below — trap-proof, so no
// media-query re-assertion is needed. Colour, and the centring, live here. The 278px max-width
// matches the Figma heading text box so it wraps "How our recipe / boxes work" on every breakpoint;
// left-anchored on mobile/tablet, centred (margin auto) on desktop.
const SectionHeading = withCitrusTheme(Heading2, (theme) => ({
  color: '#1A2026',
  maxWidth: '278px',
  marginLeft: 0,
  marginRight: 0,
  [`@media (min-width: ${theme.tokens.breakpoints[2]})`]: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}))

// Section background per Figma 320:11419 ("Section" fill #F2F3F4). Not a Citrus colour token, so it's
// set exactly here.
const Section = withCitrusTheme('section', () => ({
  backgroundColor: '#F2F3F4',
}))

// Gutters live on each child, not the shared row (mirrors CWCSuppliersSection's CarouselColumn), so
// the carousel can bleed to the right edge while the heading and CTA keep their own inset (16px
// mobile, 32px tablet, 0 desktop — the row's old shared paddingH value).
// Left gutter only, matching the heading/CTA columns — no right padding, so the carousel bleeds.
const CarouselWrapper = withCitrusTheme(Box, (theme) => [
  { paddingRight: 0 } as CSSObject,
  theme.responsiveSpacing([4, null, 8, 0], ['paddingLeft']) as CSSObject,
])

export function CWCHowItWorksSection() {
  const trackCTA = () => {
    trackCTAClick(
      TrackingActions.HOW_GOUSTO_WORKS_CLICKED,
      LandingPageContentSections.HOW_RECIPE_BOXES_WORK,
    )
  }

  return (
    <Section data-testid="cwcHowItWorksSection">
      {/* Top/bottom padding matches the platform standard used across the CwC homepage: 56px at
          mobile/640/tablet, 80px at desktop (see CWCWeeklyMenu/CWCHomepageReviewsSection). */}
      <Box paddingV={[14, null, null, 20]}>
        {/* Horizontal inset on mobile/tablet so the heading aligns to the frame edge (16px mobile,
            32px tablet) and wraps like the design; desktop uses the full width and centres it. */}
        <Box paddingH={[4, null, 8, 0]}>
          <SectionHeading
            size={13}
            textAlign={[TextAlign.Left, null, null, TextAlign.Center]}
            fontFamily={FontFamily.ExtraBold}
            fontWeight={FontWeight.Bold}
          >
            {CWC_HOW_IT_WORKS_SECTION.heading}
          </SectionHeading>
        </Box>
        <Space size={6} />
        <CarouselWrapper>
          <CWCHowItWorksCarousel />
        </CarouselWrapper>
        <Space size={6} />
        <Box
          paddingH={[4, null, 8, 0]}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
        >
          <CWCLinkButton
            href={CWC_HOW_IT_WORKS_SECTION.cta.href}
            onClick={trackCTA}
            variant="secondary"
          >
            {CWC_HOW_IT_WORKS_SECTION.cta.label}
          </CWCLinkButton>
        </Box>
      </Box>
    </Section>
  )
}
